import { useRef, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

import { Icon, IconNames } from '@sendible/design-system/src';
import {
  Container,
  ScrollViewport,
  CardScrollArea,
  ScrollButton,
  ScrollFade,
  Card,
  HeaderPanel,
  PanelTopRow,
  CardDate,
  CardLink,
  ProfileSection,
  Avatar,
  PlatformBadge,
  ProfileInfo,
  ProfileName,
  ProfileHandle,
  CaptionRow,
  CaptionText,
  Thumbnail,
  PlayOverlay,
  MetricsList,
  MetricRow,
  MetricLabel,
  MetricValue,
  EmptyState,
} from './index.styles';
import { PLATFORM_COLOR_ICON_MAP, resolveMetricLabel } from '../../../../helpers';
import ProfileIcons from '../../../ProfileIcons';

const formatPostDate = (dateStr: string): string => DateTime.fromISO(dateStr).toFormat('d MMM yyyy - h.mm a');
const formatMetricValue = (value: number, type: string): string => {
  if (type === 'percentage') return `${(value * 100).toFixed(1)}%`;
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;

  return value.toLocaleString();
};

export const PostsCardListRenderer = ({ data, selectedProfiles, supportedPlatforms, onFetchNextPage, hasNextPage }: DynamicModuleBuilderProps) => {
  const { t } = useTranslation('reports');

  // Derive defensively BEFORE hooks so the early-return below doesn't break rules-of-hooks.
  const moduleData = data && data.chartType === 'PostsCardList' ? (data as PostsCardListModuleData) : null;
  const posts = moduleData?.data ?? [];
  const metrics = moduleData?.metrics ?? [];

  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Updates left/right scroll arrow visibility. Stable — no deps.
  const updateAffordances = useCallback(() => {
    const el = scrollRef.current;

    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  // Separate listener for infinite-scroll: re-registers whenever hasNextPage or onFetchNextPage changes.
  const handleScrollForPagination = useCallback(() => {
    const el = scrollRef.current;

    if (!el || !hasNextPage || !onFetchNextPage) return;

    const isAtEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 20;

    if (isAtEnd) onFetchNextPage();
  }, [hasNextPage, onFetchNextPage]);

  useEffect(() => {
    const el = scrollRef.current;

    if (!el) return;

    updateAffordances();

    el.addEventListener('scroll', updateAffordances, { passive: true });
    el.addEventListener('scroll', handleScrollForPagination, { passive: true });

    const ro = new ResizeObserver(updateAffordances);

    ro.observe(el);

    return () => {
      el.removeEventListener('scroll', updateAffordances);
      el.removeEventListener('scroll', handleScrollForPagination);
      ro.disconnect();
    };
  }, [updateAffordances, handleScrollForPagination, posts.length]);

  if (!moduleData || posts.length === 0) {
    return <EmptyState>{t('new_reports_no_posts_to_display')}</EmptyState>;
  }

  const metricTypeMap = new Map(metrics.map((m) => [m.name, m.type]));

  const scrollByPage = (dir: 1 | -1) => {
    const el = scrollRef.current;

    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: 'smooth' });
  };

  return (
    <>
      <Container>
        <ScrollViewport>
          <CardScrollArea ref={scrollRef}>
            {posts.map((post) => (
              <Card key={post.id}>
                <HeaderPanel>
                  <PanelTopRow>
                    <CardDate>{formatPostDate(post.publishedAt)}</CardDate>
                    {post.postUrl && (
                      <CardLink
                        href={post.postUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Open post"
                      >
                        <Icon
                          color="brandDarkTint30"
                          name="arrow_up_right"
                        />
                      </CardLink>
                    )}
                  </PanelTopRow>
                  {post.profile && (
                    <ProfileSection>
                      <Avatar>
                        <img
                          src={post.profile.profileImageUrl}
                          alt={post.profile.displayName}
                          loading="lazy"
                        />
                        <PlatformBadge data-testid="post-profile-platform-badge">
                          <Icon name={(PLATFORM_COLOR_ICON_MAP[post.profile.profileType] ?? 'user') as IconNames} />
                        </PlatformBadge>
                      </Avatar>
                      <ProfileInfo>
                        <ProfileName>{post.profile.displayName}</ProfileName>
                        <ProfileHandle>@{post.profile.username}</ProfileHandle>
                      </ProfileInfo>
                    </ProfileSection>
                  )}
                  <CaptionRow>
                    <CaptionText>{post.caption}</CaptionText>
                    {post.thumbnailUrl && (
                      <Thumbnail>
                        <img
                          src={post.thumbnailUrl}
                          alt=""
                          loading="lazy"
                        />
                        <PlayOverlay>
                          <Icon
                            color="brandLight"
                            name="play"
                          />
                        </PlayOverlay>
                      </Thumbnail>
                    )}
                  </CaptionRow>
                </HeaderPanel>

                <MetricsList>
                  {post.metrics.map((m) => {
                    const type = metricTypeMap.get(m.metric) ?? 'number';

                    return (
                      <MetricRow key={m.metric}>
                        <MetricLabel>{resolveMetricLabel(m.metric, t)}</MetricLabel>
                        <MetricValue>{formatMetricValue(m.value, type)}</MetricValue>
                      </MetricRow>
                    );
                  })}
                </MetricsList>
              </Card>
            ))}
          </CardScrollArea>

          {canScrollLeft && (
            <>
              <ScrollFade $side="left" />
              <ScrollButton
                $side="left"
                onClick={() => scrollByPage(-1)}
                aria-label="Scroll left"
              >
                <Icon
                  color="brandDarkTint30"
                  name="arrow_left"
                />
              </ScrollButton>
            </>
          )}

          {canScrollRight && (
            <>
              <ScrollFade $side="right" />
              <ScrollButton
                $side="right"
                onClick={() => scrollByPage(1)}
                aria-label="Scroll right"
              >
                <Icon
                  color="brandDarkTint30"
                  name="arrow_right"
                />
              </ScrollButton>
            </>
          )}
        </ScrollViewport>
      </Container>
      <ProfileIcons
        selectedProfiles={selectedProfiles}
        supportedPlatforms={supportedPlatforms}
      />
    </>
  );
};
